import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "./Layout";
import { useNavigate } from "react-router-dom";
import { useAuth } from './StoreValue';
import { markStepComplete } from './StepGuard';
import { getSessionStorageWithExpiry } from '../utils/storage';
import { getAgentConfig, isPromotix } from '../utils/agentConfig';

const BankInfo = () => {
  
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState({});
  const [loading, setLoading] = useState(false);  // Add loading state
  const [isRoutingSynced, setIsRoutingSynced] = useState(false);
  const [isAccountSynced, setIsAccountSynced] = useState(false);
  const [isDdaUseSynced, setIsDdaUseSynced] = useState(false);
  const [isAccountTypeSynced, setIsAccountTypeSynced] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const accountUses = [
    "AchDeposits",
    "AchFees",
    "AchWithdrawals",
    "CreditDeposits",
    "CreditExceptions",
    "CreditFees",
  ];
  const { ContactGuid,agentIdVal,leadGuid, updateAgentId, updateContactGuid, updateLeadGuid } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/Auth/LeadLookup`, {
                params: {
                    leadGuidVal: leadGuid,
                    agentIdValue: agentIdVal,
                    // Pass leadGuid here
                },
                });
      const leadData = response.data;
  
      if (leadData.result.responseCode === "00") {
        let batches = leadData.queryResponse.batches;
  
        if (!Array.isArray(batches)) {
          batches = [batches];
        }
  
        const firstBatchType = batches[0]?.batch[0]?.batchType || null;
        const secondBatchType = batches[0]?.batch[1]?.batchType || null;
  
  
        // Ensure 'accounts' exists before attempting to access 'account'
        const accountsData =
          leadData.queryResponse.accounts && leadData.queryResponse.accounts.account
            ? Array.isArray(leadData.queryResponse.accounts.account)
              ? leadData.queryResponse.accounts.account
              : []
            : [];
  
  
        // Filter accounts based on batch types and ensure the account is visible, even if data is null
        const filteredAccounts = accountUses.reduce((acc, accountUse) => {
          const account = accountsData.find((a) => a.accountUse === accountUse) || null;
  
          // Debug: Check the accountUse and batch types being compared
          // Check for "Credit" in either batch
          const isCreditAccountVisible =
            (firstBatchType === "Credit" || secondBatchType === "Credit") &&
            ["CreditDeposits", "CreditFees", "CreditExceptions"].includes(accountUse);
  
          // Check for "ACH" in either batch
          const isAchAccountVisible =
            (firstBatchType === "Ach" || secondBatchType === "Ach") &&
            ["AchDeposits", "AchFees", "AchWithdrawals"].includes(accountUse);
          const isVisible = isCreditAccountVisible || isAchAccountVisible;
  
  
          if (isVisible) {
            acc[accountUse] = {
              guid: account?.guid || "", // If account data is missing, use empty string
              routingNumber: account?.aba || "", // If account data is missing, use empty string
              accountNumber: account?.dda || "", // If account data is missing, use empty string
              originalAccountNumber: account?.dda || "", // Snapshot of the loaded (possibly masked) DDA; used to detect real edits on submit
              ddaUse: account?.ddaUse || "B", // If account data is missing, use empty string
              accountType: account?.ddaType || "C", // If account data is missing, use empty string
              required: true,
            };
          }
  
          return acc;
        }, {});
  
        setAccounts(filteredAccounts); // Set filtered accounts, including the ones with missing data
      } else {
        throw new Error("Invalid response code");
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setErrorMessage("Failed to fetch account data.");
    }
  };
  
  useEffect(() => {
    fetchAccounts();
  }, []);

  const valueAgentId = getSessionStorageWithExpiry("agentId");
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    if (!leadGuid) {
        setErrorMessage('Session expired or lead data is missing. Please start over from the login page.');
        return;
    }
    setLoading(true);

    try {
        const filteredAccounts = Object.entries(accounts).filter(([accountUse, account]) => {
            const isVisible = account.required || account.selected || false;
            return isVisible;
        });

        if (filteredAccounts.length === 0) {
            throw new Error("No valid bank information to submit.");
        }

        const accountRequests = filteredAccounts.map(([accountUse, account]) => {
            // Send the account number (Dda) for new accounts, or when the user changed an
            // existing one to a real (non-masked) value. A masked/unchanged value is omitted
            // so TRX keeps its stored number — re-sending a masked "****1234" would corrupt it.
            const sendDda = !account.guid ||
                (account.accountNumber &&
                    !account.accountNumber.includes("*") &&
                    account.accountNumber !== (account.originalAccountNumber || ''));

            return {
                leadguidVal: leadGuid,
                agentId: agentIdVal,
                ContactGuidVal: ContactGuid,
                Detail: {
                    TranType: "TranType",
                    TranAction: "TranAction",
                    Ip: "Ip",
                },
                Account: {
                    Aba: account.routingNumber,
                    DdaUse: account.ddaUse,
                    DdaType: account.accountType,
                    ...(account.guid ? { Guid: account.guid } : { AccountUse: accountUse }),
                    ...(sendDda ? { Dda: account.accountNumber } : {}),
                },
            };
        });

        async function delay(ms) {
          return new Promise(resolve => setTimeout(resolve, ms));
      }
      
      const responses = [];
      for (const payload of accountRequests) {
          try {
              const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, payload);
              const errorInfo = response.data.response.result?.debugInfo;
              responses.push(response.data.response.result);
          } catch (errorInfo) {
              responses.push({ responseCode: "ERROR", errorInfo: errorInfo.message || "Request failed" });
          }
          await delay(2000); // 2-second delay between requests
      }
      
        // Check if all responses are "00"
        const allSuccess = responses.every((res) => res.responseCode === "00");

        if (!allSuccess) {
            const errorMessages = responses
                .filter((res) => res.responseCode !== "00")
                .map((res) => res.debugInfo
                 || "Unknown error");
            throw new Error(errorMessages.join(", "));
        }

        // ✅ Proceed only if all responses are "00"
        markStepComplete('bankInfo');

        const agentConfig = getAgentConfig(valueAgentId);

        if (agentConfig.completionPath === 'autocomplete') {
            // Silent Pricing Clone — same API call PricingInfo.js makes for rev.io agents
            const pricingData = {
                leadguidVal: leadGuid,
                agentId: agentIdVal,
                Detail: { TranType: "TranType", TranAction: "TranAction", Ip: 'Ip' },
                QueryRequest: { Detail: { Client: "Client", ClientBranch: "Client", Active: "1" } }
            };
            const pricingResponse = await axios.post(`${API_BASE_URL}/api/Auth/PricingManage`, pricingData);
            if (pricingResponse.data !== 'OK') {
                setErrorMessage(typeof pricingResponse.data === 'string' ? pricingResponse.data : 'Pricing setup failed.');
                return;
            }
        }

        if (agentConfig.completionPath === 'extended') {
            // Promotix already completed Pricing right after Client, so go straight to Terms.
            navigate(isPromotix(valueAgentId) ? "/TermsAndConditions" : "/PricingInfo");
        } else {
            // 'standard' or 'autocomplete' — these flows end at Bank. Mark the skipped steps complete
            // ONLY when we actually reach /Completed; marking them earlier left the sidebar green when
            // the alert call below failed and the user was stranded on the Bank page.
            const completeApplication = () => {
                markStepComplete('pricingInfo');
                markStepComplete('termsAndConditions');
                markStepComplete('eSignature');
                navigate("/Completed");
            };

            if (!agentConfig.sendAlertEmail) {
                completeApplication();
                return;
            }

            try {
                const alertData = {
                  leadguidVal: leadGuid,
                    agentId: agentIdVal,
                    Detail: {
                        TranType: "Alert",
                        TranAction: "Query",
                        Ip: "Ip",
                    },
                    QueryRequest: {
                        Detail: {
                            Client: agentIdVal,
                            Active: "1",
                        },
                        Alert: {
                            Description: "Completed Merchant Application",
                        },
                    },
                };

                const response1 = await axios.post(`${API_BASE_URL}/api/Auth/AlertQuery`, alertData);
                if (response1.data === "00") {
                    completeApplication();
                } else {
                    const alertMsg = typeof response1.data === 'string' ? response1.data : JSON.stringify(response1.data);
                    setErrorMessage(alertMsg || 'Alert Query failed.');
                }
            } catch (alertError) {
                const serverMsg = alertError.response?.data?.message || alertError.response?.data || alertError.message;
                setErrorMessage(typeof serverMsg === 'string' ? serverMsg : 'Failed to send alert.');
            }
        }
    } catch (error) {
        console.error("Error submitting form:", error);
        const serverMsg = error.response?.data?.message || error.response?.data || error.message;
        setErrorMessage(typeof serverMsg === 'string' ? serverMsg : 'An unexpected error occurred.');
    } finally {
        setLoading(false);
    }
};
  
const handleBlur = (field, value) => {
  // Check if any account already has a value for this field (other than the current one)
  const anyValueExists = Object.values(accounts).some(acc => acc[field] && acc[field] !== value);

  // Only update if NO value exists for this field in any account
  if (!anyValueExists) {
    setAccounts((prevAccounts) => {
      const updatedAccounts = {};
      Object.keys(prevAccounts).forEach((accountUse) => {
        if (Object.keys(prevAccounts[accountUse]).length === 0) {
          updatedAccounts[accountUse] = {
            [field]: value,
          };
        } else {
          updatedAccounts[accountUse] = {
            ...prevAccounts[accountUse],
            [field]: value,
          };
        }
      });
      return updatedAccounts;
    });
  }
  // If a value exists, do nothing (prevent overwrite)
};
  

  const handleInputChange = (accountUse, field, value) => {
    setAccounts((prevAccounts) => ({
      ...prevAccounts,
      [accountUse]: {
        ...prevAccounts[accountUse],
        [field]: value,
      },
    }));
  };

  const removeAccountForm = async (accountGuid) => {
  setLoading(true); 
  setErrorMessage(''); 
  setSuccessMessage(''); 

  try {
    // Only call API if the account has a guid (i.e., it exists in backend)
    if (accountGuid) {
      const data = {
        leadguidVal: leadGuid,
        agentId: agentIdVal,
        ContactGuidVal: ContactGuid, 
        Detail: {
          TranType: "Remove",    
          TranAction: "RemoveAccount", 
          Ip: "Ip",
        },
        Account: {
          Guid: accountGuid, 
        },
      };

      // Send the API request
      const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, data);

      var responseCode = response.data.response.result.responseCode;
      var errorInfo = response.data.response.result.errorInfo;

      if (responseCode !== '00' && responseCode !== 'X7') {
        setErrorMessage(errorInfo || 'Failed to remove account from API.');
        setLoading(false);
        return;
      }
    }

    // Clear the values for the account, but keep the row/label
    setAccounts(prevAccounts => {
      const updatedAccounts = { ...prevAccounts };
      Object.keys(updatedAccounts).forEach(accountUse => {
        if (updatedAccounts[accountUse].guid === accountGuid) {
          updatedAccounts[accountUse] = {
            ...updatedAccounts[accountUse],
            guid: "",
            routingNumber: "",
            accountNumber: "",
            ddaUse: "B",        // or your default
            accountType: "C",  // or your default
          };
        }
      });
      return { ...updatedAccounts };
    });

    setSuccessMessage('Account values cleared!');
    setTimeout(() => setSuccessMessage(''), 3000);

  } catch (error) {
    console.error("Error removing account:", error);
    setErrorMessage("An error occurred while removing the account. Please try again.");
  } finally {
    setLoading(false); // Stop loading after response
  }
};

  return (
    <div className={`signature-container}`}>
          {loading && (
                <div className="loading-overlay">
                <div className="spinner-border" role="status">
                </div>
                </div>
        )}
      <Layout pageTitle="Bank Information">
        <form onSubmit={handleSubmit}>
          {/* Table Header */}
          <div className="table-container">
            <div className="table-header table-row">
              <div className="table-cell">Account Use</div>
              <div className="table-cell">Routing Number *</div>
              <div className="table-cell">Account Number *</div>
              <div className="table-cell">DDA Use</div>
              <div className="table-cell">Account Type</div>
              <div className="table-cell">Delete</div>
            </div>
  
            {/* Rows for Each Account */}
            {Object.entries(accounts).map(([accountUse, account]) => (
              <div key={accountUse} className="table-row">
                {/* Account Use */}
                <div className="table-cell">{accountUse}</div>
            
                {/* Routing Number */}
                <div className="table-cell">
                  <input
                    type="text"
                    value={account.routingNumber}
                    className={`form-controling ${
                      !account.routingNumber ? "is-invalid" : ""
                    }`}
                    onChange={(e) =>
                      handleInputChange(accountUse, "routingNumber", e.target.value)
                    }
                    onBlur={(e) => handleBlur("routingNumber", e.target.value)}
                    required
                  />
                </div>
  
                {/* Account Number */}
                <div className="table-cell">
                  <input
                    type="text"
                    value={account.accountNumber}
                    className={`form-controling ${
                      !account.accountNumber ? "is-invalid" : ""
                    }`}
                    onChange={(e) =>
                      handleInputChange(accountUse, "accountNumber", e.target.value)
                    }
                    onBlur={(e) => handleBlur("accountNumber", e.target.value)}
                    required
                  />
                </div>
  
                {/* DDA Use */}
                <div className="table-cell">
                  <div>
                    <label className='label_Control'>
                      <input
                        type="radio"
                        name={`ddaUse-${accountUse}`}
                        value="B"
                        checked={account.ddaUse === "B"}
                        onChange={(e) =>
                          handleInputChange(accountUse, "ddaUse", e.target.value)
                        }
                      />
                      Business
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`ddaUse-${accountUse}`}
                        value="P"
                        checked={account.ddaUse === "P"}
                        onChange={(e) =>
                          handleInputChange(accountUse, "ddaUse", e.target.value)
                        }
                      />
                      Personal
                    </label>
                  </div>
                </div>
                {/* Account Type */}
                <div className="table-cell">
                  <div>
                    <label className='label_Control'>
                      <input
                        type="radio"
                        name={`accountType-${accountUse}`}
                        value="C"
                        checked={account.accountType === "C"}
                        onChange={(e) =>
                          handleInputChange(accountUse, "accountType", e.target.value)
                        }
                      />
                      Checking
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`accountType-${accountUse}`}
                        value="S"
                        checked={account.accountType === "S"}
                        onChange={(e) =>
                          handleInputChange(accountUse, "accountType", e.target.value)
                        }
                      />
                      Savings
                    </label>
                  </div>
                </div>
                <div className="batch-header d-flex align-items-center justify-content-between">
    <button
      type="button"
      className="btn btn-danger mt-2 ms-3"
      onClick={()=>removeAccountForm(account.guid)}
    >
      Remove
    </button></div>
              </div>
            ))}
          </div>
  
          {/* Submit Button */}
          <div className="d-flex justify-content-center mt-3">
            <button type="submit" className="btn btn-success px-5" style={{ width: "370px" }}>
              Next
            </button>
          </div>
        </form>
  {/* Show loading image when waiting for response */}
        {errorMessage && <p className="text-danger">{errorMessage}</p>}
        {successMessage && <p className="text-success">{successMessage}</p>}
      </Layout>
    </div>
  );
  
}  

export default BankInfo;
